import { useEffect, useState } from "react";
import api from "./api";

// Shared branding state (logo_url, logo_url_light, favicon_url), fetched once
// and kept live via the "branding_updated" window event.
let cache = null;
const listeners = new Set();

export const assetUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//.test(u) || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
};

export const applyFavicon = (url) => {
  const href = assetUrl(url);
  if (!href) return;
  // Guess the correct MIME type from the file extension so browsers pick the right decoder
  // (svg/png/ico). Without this Chrome silently falls back to the static /favicon.ico.
  const ext = (href.split("?")[0].split(".").pop() || "").toLowerCase();
  const mime = ext === "svg" ? "image/svg+xml"
             : ext === "png" ? "image/png"
             : ext === "webp" ? "image/webp"
             : ext === "ico" ? "image/x-icon"
             : "image/png";
  // Remove ALL existing rel=icon / rel=shortcut icon links so the admin-uploaded icon
  // is the ONLY one in the head. Otherwise the static index.html <link>s win.
  document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"]').forEach((el) => el.remove());
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = mime;
  link.href = href;
  document.head.appendChild(link);
};

// Favicon comes strictly from the admin-uploaded favicon_url. The browser tab
// icon is intentionally independent of the primary logo (which is typically a
// wide wordmark that would not render well at 16×16).
const _pickFaviconSource = (b) => b.favicon_url || "";

export function loadBranding() {
  return api.get("/settings").then(({ data }) => {
    cache = data.branding || {};
    const src = _pickFaviconSource(cache);
    if (src) applyFavicon(src);
    listeners.forEach((fn) => fn(cache));
    return cache;
  }).catch(() => ({}));
}

export function useBranding() {
  const [branding, setBranding] = useState(cache || {});
  useEffect(() => {
    const update = (b) => setBranding(b || {});
    listeners.add(update);
    if (cache) setBranding(cache); else loadBranding();
    const onEvt = (e) => {
      cache = { ...(cache || {}), ...(e.detail || {}) };
      // Live-update the browser tab too when an admin uploads a new icon in Settings.
      const src = _pickFaviconSource(cache);
      if (src) applyFavicon(src);
      setBranding(cache);
    };
    window.addEventListener("branding_updated", onEvt);
    return () => { listeners.delete(update); window.removeEventListener("branding_updated", onEvt); };
  }, []);
  return branding;
}
